import { ChangeEvent, CSSProperties, DragEvent, MouseEvent, useCallback, useEffect, useRef, useState } from "react";
import { Button, keyframes, styled, SxProps, Theme } from "@mui/material";
import Cropper from "react-easy-crop";
import { ActionBtn } from "../../../components/Typography";
import { Section } from "../index";
import { Area, Point } from "react-easy-crop/types";
import getCroppedImg from "../cropImage";
import { sizing } from "../../../utils/methods";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { ClickableContainer } from "../../../utils/table-schemas";

type ContentUploadProps = {
  onUpload?(): Promise<any>;
  setPreview?(value: any): void;
  setFile?(file: File | undefined): void;
  sx?: SxProps<Theme>;
  additionalSx?: SxProps<Theme>;
  cropperStyle?: CSSProperties;
  onlyImages?: boolean;
  onlyGifs?: boolean;
  draftImage?: any;
  profileImage?: boolean;
  title?: string;
  loading?: boolean;
  description?: string;
};

type UploadStatus = "idle" | "dragging" | "uploading" | "paused" | "cancelled" | "completed";

const MAX_FILE_SIZE = 100_000_000;

const truncate = (name: string): string => (name.length > 30 ? `${name.slice(0, 28)}…` : name);

const ContentUploadNew = ({
  sx,
  setPreview,
  draftImage,
  setFile,
  onlyImages,
  onlyGifs,
  cropperStyle,
  profileImage,
  onUpload,
  loading,
  title,
  description,
}: ContentUploadProps) => {
  const uploadRef = useRef<HTMLInputElement>(null);
  const CroppingBlockRef = useRef<HTMLDivElement>(null);

  const [selectedFile, setSelectedFile] = useState<File | undefined>(undefined);
  const [filePreview, setFilePreview] = useState<string | undefined>(undefined);
  const [error, setError] = useState("");
  const [cropping, setCropping] = useState<boolean>(false);
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [croppedImage, setCroppedImage] = useState<File | undefined>(undefined);
  const [croppedImagePreview, setCroppedImagePreview] = useState<string | undefined>(undefined);

  // Upload simulator state — mirrors SignerCard's UploadDropZone behaviour.
  const [uploadStatus, setUploadStatusRaw] = useState<UploadStatus>("idle");
  const [progress, setProgress] = useState(0);
  const [uploadFilename, setUploadFilename] = useState("");
  const [speed, setSpeed] = useState("— MB/s");
  const [eta, setEta] = useState("Calculating…");

  const statusRef = useRef<UploadStatus>("idle");
  const progressRef = useRef(0);
  const pendingFileRef = useRef<File | null>(null);
  const intervalRef = useRef<number | null>(null);

  const setUploadStatus = useCallback((next: UploadStatus) => {
    statusRef.current = next;
    setUploadStatusRaw(next);
  }, []);

  const updateProgress = useCallback((next: number) => {
    progressRef.current = next;
    setProgress(next);
  }, []);

  const clearTick = useCallback(() => {
    if (intervalRef.current !== null) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Treat a pre-uploaded draft as already completed.
  useEffect(() => {
    if (draftImage) {
      setFilePreview(draftImage?.url);
      setCroppedImage(draftImage);
      setCroppedImagePreview(draftImage?.url);
      setUploadFilename(draftImage?.name || "Uploaded image");
      setUploadStatus("completed");
      updateProgress(100);
      if (setFile) {
        setFile(draftImage);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftImage]);

  const onCropComplete = (_croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels as any);
  };

  const showCroppedImage = async () => {
    try {
      const croppedImage = await getCroppedImg(filePreview, croppedAreaPixels);
      setCroppedImage(croppedImage as File);
      setCroppedImagePreview(URL.createObjectURL(croppedImage as Blob));
      setSelectedFile(croppedImage as File);
      if (setFile) {
        setFile(croppedImage as File);
      }
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (selectedFile?.type !== "image/gif" && filePreview && !croppedImage) {
      setCropping(true);
    } else {
      if (croppedImage) {
        setCropping(false);
      }
    }
  }, [selectedFile, filePreview, croppedImage]);

  useEffect(() => {
    if (!filePreview) {
      setCroppedImage(undefined);
      setCroppedImagePreview(undefined);
      setCropping(false);
    }
  }, [filePreview]);

  useEffect(() => {
    if (!selectedFile) {
      setFilePreview(undefined);
      setCroppedImagePreview(undefined);
      if (setPreview) {
        setPreview(undefined);
      }
      return;
    }

    const objectUrl = URL.createObjectURL(selectedFile);
    setFilePreview(objectUrl);
    if (setPreview) {
      setPreview(objectUrl);
    }

    return () => URL.revokeObjectURL(objectUrl);
  }, [selectedFile]);

  useEffect(() => () => clearTick(), [clearTick]);

  const handleButtonClick = () => {
    uploadRef.current?.click();
  };

  const resetState = useCallback(() => {
    clearTick();
    pendingFileRef.current = null;
    setUploadFilename("");
    setSpeed("— MB/s");
    setEta("Calculating…");
    updateProgress(0);
    setUploadStatus("idle");
    setSelectedFile(undefined);
    setCroppedImage(undefined);
    setCroppedImagePreview(undefined);
    setFilePreview(undefined);
    setCropping(false);
    setError("");
    if (uploadRef.current) {
      // @ts-ignore
      uploadRef.current.value = null;
    }
    if (setFile) {
      setFile(undefined);
    }
  }, [clearTick, setFile, setUploadStatus, updateProgress]);

  const handleProfileUpload = async () => {
    if (onUpload) {
      await onUpload();
    }
  };

  // Finalize the upload once the simulator hits 100%. Runs validation
  // (GIF dimension check) before committing the file to selectedFile.
  const completeUpload = useCallback(() => {
    const file = pendingFileRef.current;
    if (!file) return;

    if (file.type === "image/gif" && setFile) {
      const img = new Image();
      const reader = new FileReader();
      reader.onload = function (e) {
        img.src = e.target?.result as any;
        img.onerror = () => {
          setError("Failed to read GIF image");
          setUploadStatus("cancelled");
        };
        img.onload = () => {
          const width = img.width;
          const height = img.height;
          if (width < 200) {
            setError(
              `Selected GIF resolution is too small (${width}x${height})! Should be at least 200x200 px`
            );
            setUploadStatus("cancelled");
          } else {
            setUploadStatus("completed");
            setSelectedFile(file);
            setFile(file);
          }
        };
      };
      reader.onerror = () => {
        setError("Failed to read GIF file");
        setUploadStatus("cancelled");
      };
      reader.readAsDataURL(file);
    } else {
      setUploadStatus("completed");
      setSelectedFile(file);
    }
  }, [setFile, setUploadStatus]);

  const finishUpload = useCallback(() => {
    clearTick();
    updateProgress(100);
    setSpeed("");
    setEta("");
    completeUpload();
  }, [clearTick, completeUpload, updateProgress]);

  const runTicker = useCallback(() => {
    clearTick();
    intervalRef.current = window.setInterval(() => {
      if (statusRef.current !== "uploading") return;
      const next = Math.min(progressRef.current + Math.random() * 6 + 2, 100);
      updateProgress(next);
      const spd = (Math.random() * 3 + 1.5).toFixed(1);
      const remaining = Math.round((100 - next) / 8);
      setSpeed(`${spd} MB/s`);
      setEta(remaining > 0 ? `ETA ${remaining}s` : "Almost…");
      if (next >= 100) {
        clearTick();
        window.setTimeout(finishUpload, 300);
      }
    }, 80);
  }, [clearTick, finishUpload, updateProgress]);

  const startUpload = useCallback(
    (file: File) => {
      if (statusRef.current === "uploading" || statusRef.current === "paused") return;
      pendingFileRef.current = file;
      setUploadFilename(truncate(file.name));
      updateProgress(0);
      setSpeed("— MB/s");
      setEta("Calculating…");
      setError("");
      setUploadStatus("uploading");
      runTicker();
    },
    [runTicker, setUploadStatus, updateProgress]
  );

  const handleFile = (file: File) => {
    const heicTypes = ["image/heic", "image/heif"];
    const heicExts = [".heic", ".heif"];
    const isHeic = heicTypes.includes(file.type) || heicExts.some((ext) => file.name.toLowerCase().endsWith(ext));

    if (file.size > MAX_FILE_SIZE) {
      setError(`Selected file is too big! Size should not exceed ${MAX_FILE_SIZE / 1_000_000} MB`);
      return;
    }
    if (isHeic) {
      setError(
        "HEIC/HEIF images are not supported for preview or cropping. " +
          "Please convert to JPEG or PNG and try again. "
      );
      return;
    }
    if (onlyImages && file.type === "image/gif") {
      setError("GIF Files are not allowed for profile pictures");
      return;
    }
    if (onlyGifs && file.type !== "image/gif") {
      setError("Only GIF files are allowed for this upload");
      return;
    }

    startUpload(file);
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) handleFile(f);
    e.target.value = "";
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (statusRef.current === "idle" || statusRef.current === "dragging") {
      setUploadStatus("dragging");
    }
  };

  const handleDragLeave = () => {
    if (statusRef.current === "dragging") setUploadStatus("idle");
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (statusRef.current === "dragging") setUploadStatus("idle");
    if (statusRef.current === "uploading" || statusRef.current === "paused") return;
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  };

  const handleZoneClick = () => {
    if (statusRef.current === "idle" || statusRef.current === "dragging") {
      handleButtonClick();
    }
  };

  const togglePause = (e: MouseEvent) => {
    e.stopPropagation();
    if (statusRef.current === "uploading") {
      setUploadStatus("paused");
      setSpeed("— MB/s");
      setEta("Paused");
    } else if (statusRef.current === "paused") {
      setUploadStatus("uploading");
    }
  };

  const cancelUpload = (e: MouseEvent) => {
    e.stopPropagation();
    clearTick();
    setUploadStatus("cancelled");
    setSpeed("");
    setEta("");
  };

  const retryUpload = (e: MouseEvent) => {
    e.stopPropagation();
    const file = pendingFileRef.current;
    setError("");
    if (file) {
      setUploadStatus("idle");
      startUpload(file);
    }
  };

  const dismissCancel = (e: MouseEvent) => {
    e.stopPropagation();
    resetState();
  };

  const replaceCompleted = (e: MouseEvent) => {
    e.stopPropagation();
    resetState();
    handleButtonClick();
  };

  const removeCompleted = (e: MouseEvent) => {
    e.stopPropagation();
    resetState();
  };

  const openFile = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      const url = croppedImagePreview || filePreview || draftImage?.url;
      if (url) window.open(url, "_blank", "noopener,noreferrer");
    },
    [croppedImagePreview, filePreview, draftImage]
  );

  const thumbUrl = croppedImagePreview || filePreview || draftImage?.url;
  const rawFileName =
    uploadFilename || selectedFile?.name || draftImage?.name || "Uploaded image";
  const displayName = truncate(rawFileName);

  const formatTags: string[] = onlyGifs ? ["GIF"] : onlyImages ? ["JPG", "PNG"] : ["JPG", "PNG", "GIF"];
  let accept = "image/png, image/gif, image/jpeg";
  if (onlyGifs) {
    accept = "image/gif";
  } else if (onlyImages) {
    accept = "image/png, image/jpeg";
  }

  const showIdle = uploadStatus === "idle" || uploadStatus === "dragging";
  const isUploading = uploadStatus === "uploading";
  const isPaused = uploadStatus === "paused";
  const isCancelled = uploadStatus === "cancelled";
  const isCompleted = uploadStatus === "completed";
  const previewVisible = isCompleted || !!draftImage;

  return (
    <Row>
      <Column>
        <ColumnLabel>{title}</ColumnLabel>
        <Zone
          status={uploadStatus}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleZoneClick}
        >
          <HiddenInput accept={accept} ref={uploadRef} type="file" onChange={handleInputChange} />

          {showIdle && (
            <IdleWrap>
              <DropIcon dragging={uploadStatus === "dragging"}>
                <CloudUploadOutlinedIcon sx={{ fontSize: 28 }} />
              </DropIcon>
              <DropTitle>{description || "Make sure you have the right to use this image"}</DropTitle>
              <DropSub>Max 100 MB · {formatTags.join(" / ")}</DropSub>
              <BrowseBtn
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleButtonClick();
                }}
              >
                <FolderOpenOutlinedIcon sx={{ fontSize: 14 }} />
                Browse Files
              </BrowseBtn>
              <FormatTags>
                {formatTags.map((t) => (
                  <FormatTag key={t}>{t}</FormatTag>
                ))}
              </FormatTags>
              {error && <ErrorBox>{error}</ErrorBox>}
            </IdleWrap>
          )}

          {!showIdle && (
            <>
              <UploadRow>
                <FileIcon status={uploadStatus}>
                  {isCompleted && thumbUrl ? (
                    <PreviewThumb src={thumbUrl} alt={displayName} />
                  ) : (
                    <CloudUploadOutlinedIcon sx={{ fontSize: 20 }} />
                  )}
                </FileIcon>
                <UploadBody>
                  <UploadTop>
                    {isCompleted ? (
                      <ClickableContainer
                        onClick={openFile}
                        title={rawFileName}
                        sx={{ maxWidth: "55%", minWidth: 0, alignItems: "center" }}
                      >
                        <Filename>{displayName}</Filename>
                      </ClickableContainer>
                    ) : (
                      <Filename title={rawFileName}>{displayName}</Filename>
                    )}
                    <UploadRight>
                      <StatusRow>
                        <StatusDot status={uploadStatus} />
                        <StatusLabel status={uploadStatus}>
                          {isPaused
                            ? "Paused"
                            : isCancelled
                            ? "Cancelled"
                            : isCompleted
                            ? "Uploaded"
                            : "Uploading"}
                        </StatusLabel>
                      </StatusRow>
                      <Pct status={uploadStatus}>{Math.round(progress)}%</Pct>
                    </UploadRight>
                  </UploadTop>
                  <Track>
                    <TrackFill status={uploadStatus} style={{ width: `${Math.round(progress)}%` }} />
                  </Track>
                  {(isUploading || isPaused) && (
                    <UploadMeta>
                      <MetaText>{speed}</MetaText>
                      <MetaText>{eta}</MetaText>
                    </UploadMeta>
                  )}
                  {isCompleted && (
                    <UploadMeta>
                      <MetaText>Ready to submit</MetaText>
                    </UploadMeta>
                  )}
                </UploadBody>

                {(isUploading || isPaused) && (
                  <CtrlGroup>
                    <CtrlBtn
                      type="button"
                      variant={isPaused ? "resume" : "pause"}
                      onClick={togglePause}
                      aria-label={isPaused ? "Resume upload" : "Pause upload"}
                    >
                      {isPaused ? (
                        <PlayArrowIcon sx={{ fontSize: 18 }} />
                      ) : (
                        <PauseIcon sx={{ fontSize: 18 }} />
                      )}
                    </CtrlBtn>
                    <CtrlBtn
                      type="button"
                      variant="cancel"
                      onClick={cancelUpload}
                      aria-label="Cancel upload"
                    >
                      <CloseIcon sx={{ fontSize: 18 }} />
                    </CtrlBtn>
                  </CtrlGroup>
                )}

                {isCompleted && (
                  <CtrlGroup>
                    <CtrlBtn type="button" variant="pause" onClick={replaceCompleted} aria-label="Replace file">
                      <RefreshIcon sx={{ fontSize: 18 }} />
                    </CtrlBtn>
                    <CtrlBtn type="button" variant="cancel" onClick={removeCompleted} aria-label="Remove file">
                      <CloseIcon sx={{ fontSize: 18 }} />
                    </CtrlBtn>
                  </CtrlGroup>
                )}
              </UploadRow>

              {isCancelled && (
                <>
                  {error && <ErrorBox>{error}</ErrorBox>}
                  <CancelledActions>
                    <RetryBtn type="button" onClick={retryUpload}>
                      <RefreshIcon sx={{ fontSize: 14 }} />
                      Retry Upload
                    </RetryBtn>
                    <DismissBtn type="button" onClick={dismissCancel}>
                      Dismiss
                    </DismissBtn>
                  </CancelledActions>
                </>
              )}
            </>
          )}
        </Zone>
      </Column>

      <Column>
        <ColumnLabel>Preview</ColumnLabel>
        <PreviewWrapper
          ref={CroppingBlockRef}
          sx={{
            p: { xs: 4, sm: 8, md: 12 },
            backgroundColor: ({ palette }) => palette.background.paper,
            borderColor: ({ palette }) => palette.action.disabled,
            ...sx,
          }}
          className={(error && isCancelled ? "error " : "") + (cropping ? "cropping" : "")}
        >
          {!!croppedImagePreview && !cropping && (
            <Preview
              className={profileImage ? "ProfileImagePreview" : ""}
              src={croppedImagePreview}
              alt={"NFT image"}
            />
          )}
          {!!filePreview && !croppedImagePreview && !cropping && (
            <Preview src={filePreview} alt={"NFT image"} />
          )}
          {cropping && (
            <Section sx={{ paddingTop: cropperStyle && sizing(0) }}>
              <Cropper
                style={{
                  containerStyle: {
                    position: "relative",
                    height: "40vh",
                    maxHeight: "400px",
                    ...cropperStyle,
                  },
                }}
                image={filePreview}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape={profileImage ? "round" : "rect"}
                onCropChange={setCrop}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
              />
              <ActionBtn
                sx={{ margin: "0 auto", width: "200px", display: "flex", alignSelf: "center" }}
                onClick={() => showCroppedImage()}
              >
                Crop
              </ActionBtn>
            </Section>
          )}
          {onUpload && profileImage && croppedImage && (
            <Button disabled={loading} sx={{ margin: 0, width: "200px" }} onClick={() => handleProfileUpload()}>
              Upload
            </Button>
          )}
          {!previewVisible && !cropping && <PreviewPlaceholder>No image yet</PreviewPlaceholder>}
        </PreviewWrapper>
      </Column>
    </Row>
  );
};

const blink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
`;

const iconPulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
`;

const borderColor = (s: UploadStatus): string => {
  if (s === "dragging" || s === "paused") return "#BC92F8";
  if (s === "uploading") return "#2B3501";
  if (s === "cancelled") return "#E24B4A";
  if (s === "completed") return "#2B7A0B";
  return "#CCCCCC";
};

const backgroundFor = (s: UploadStatus): string => {
  if (s === "dragging") return "rgba(188,146,248,0.05)";
  if (s === "idle") return "#fff";
  // File-loaded states get a subtle ~8% darker neutral so the card stands out
  // without the dashed outline.
  return "rgba(0,0,0,0.08)";
};

const Row = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: stretch;

  @media (min-width: 768px) {
    flex-direction: row;
    flex-wrap: wrap;
  }
`;

const Column = styled("div")`
  width: 100%;
  min-width: 0;
  flex: 1 1 280px;
  display: flex;
  flex-direction: column;
`;

const ColumnLabel = styled("p")`
  margin: 0 0 8px 0;
  color: #3d3d3d;
`;

const Zone = styled("div", {
  shouldForwardProp: (p) => p !== "status",
})<{ status: UploadStatus }>`
  flex: 1;
  border: 2px
    ${({ status }) =>
      status === "idle" || status === "dragging" ? `dashed ${borderColor(status)}` : "solid transparent"};
  border-radius: 16px;
  background: ${({ status }) => backgroundFor(status)};
  padding: 24px 16px;
  min-height: 200px;

  @media (min-width: 768px) {
    padding: 40px 28px;
    min-height: 300px;
  }
  text-align: center;
  cursor: ${({ status }) => (status === "idle" || status === "dragging" ? "pointer" : "default")};
  transition: border-color 0.2s, background 0.2s;
  font-family: "Inter", sans-serif;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const PreviewWrapper = styled("div")`
  flex: 1;
  border-radius: 16px;
  border: 2px dashed;
  min-height: 300px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  position: relative;

  &.error {
    border-color: ${({ theme }) => theme.palette.error.main};
  }

  p {
    margin: 0;
    color: #3d3d3d;
  }

  button {
    margin-top: 10px;
    padding: 6px 42px;
  }
`;

const PreviewPlaceholder = styled("div")`
  font-family: "Inter", sans-serif;
  font-size: 12px;
  color: #959a80;
`;

const IdleWrap = styled("div")`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const DropIcon = styled("div", { shouldForwardProp: (p) => p !== "dragging" })<{
  dragging?: boolean;
}>`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: ${({ dragging }) => (dragging ? "rgba(188,146,248,0.12)" : "#F5F5F3")};
  border: 1.5px solid ${({ dragging }) => (dragging ? "#BC92F8" : "#E6E6E2")};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  color: ${({ dragging }) => (dragging ? "#BC92F8" : "#959A80")};
  transition: all 0.2s;
`;

const DropTitle = styled("div")`
  font-size: 16px;
  font-weight: 600;
  color: #1f1f1f;
  margin-bottom: 6px;
`;

const DropSub = styled("div")`
  font-size: 12px;
  color: #959a80;
  margin-bottom: 20px;
`;

const BrowseBtn = styled("button")`
  background: #f5f5f3;
  border: 1.5px solid #e6e6e2;
  color: #1f1f1f;
  border-radius: 50px;
  font-family: "Inter", sans-serif;
  font-size: 12px;
  font-weight: 600;
  padding: 8px 20px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.13s;
  &:hover:not(:disabled) {
    border-color: #aaaaaa;
    background: #ededed;
  }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const FormatTags = styled("div")`
  display: flex;
  gap: 6px;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 14px;
`;

const FormatTag = styled("span")`
  font-family: "IBM Plex Mono", monospace;
  font-size: 10px;
  font-weight: 700;
  background: #f5f5f3;
  border: 1px solid #e6e6e2;
  color: #959a80;
  padding: 3px 8px;
  border-radius: 4px;
  letter-spacing: 0.05em;
`;

const ErrorBox = styled("div")`
  margin-top: 16px;
  background: #ffebee;
  color: #c62828;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 12px;
  font-weight: 600;
  max-width: 360px;
  word-break: break-word;
`;

const UploadRow = styled("div")`
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 12px;
  width: 100%;
  min-width: 0;
`;

const fileIconBg = (s: UploadStatus): string => {
  if (s === "uploading") return "rgba(43,53,1,0.06)";
  if (s === "paused") return "rgba(188,146,248,0.1)";
  if (s === "cancelled") return "rgba(226,75,74,0.07)";
  if (s === "completed") return "rgba(43,122,11,0.08)";
  return "#F5F5F3";
};

const fileIconBorder = (s: UploadStatus): string => {
  if (s === "uploading") return "#2B3501";
  if (s === "paused") return "#BC92F8";
  if (s === "cancelled") return "#E24B4A";
  if (s === "completed") return "#2B7A0B";
  return "#E6E6E2";
};

const fileIconColor = (s: UploadStatus): string => {
  if (s === "uploading") return "#2B3501";
  if (s === "paused") return "#7B47CC";
  if (s === "cancelled") return "#A32D2D";
  if (s === "completed") return "#2B7A0B";
  return "#959A80";
};

const FileIcon = styled("div", { shouldForwardProp: (p) => p !== "status" })<{
  status: UploadStatus;
}>`
  width: 42px;
  height: 42px;
  border-radius: 10px;
  background: ${({ status }) => fileIconBg(status)};
  border: 1.5px solid ${({ status }) => fileIconBorder(status)};
  color: ${({ status }) => fileIconColor(status)};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
  transition: all 0.2s;
  animation: ${({ status }) => (status === "uploading" ? iconPulse : "none")} 1.4s ease-in-out infinite;
`;

const PreviewThumb = styled("img")`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const UploadBody = styled("div")`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  /* push CtrlGroup to its own row on mobile */
  min-width: calc(100% - 54px);
  @media (min-width: 768px) {
    min-width: 0;
  }
`;

const UploadTop = styled("div")`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 7px;
  min-width: 0;
  gap: 8px;
`;

const Filename = styled("div")`
  font-size: 13px;
  font-weight: 600;
  color: #1f1f1f;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  flex: 1;
  text-align: left;
`;

const UploadRight = styled("div")`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;

const StatusRow = styled("div")`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const dotColor = (s: UploadStatus): string => {
  if (s === "paused") return "#BC92F8";
  if (s === "cancelled") return "#E24B4A";
  if (s === "completed") return "#2B7A0B";
  return "#2B3501";
};

const StatusDot = styled("div", { shouldForwardProp: (p) => p !== "status" })<{
  status: UploadStatus;
}>`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${({ status }) => dotColor(status)};
  flex-shrink: 0;
  transition: background 0.2s;
  animation: ${({ status }) => (status === "uploading" ? blink : "none")} 1.2s ease-in-out infinite;
`;

const labelColor = (s: UploadStatus): string => {
  if (s === "paused") return "#7B47CC";
  if (s === "cancelled") return "#A32D2D";
  return "#2B7A0B";
};

const StatusLabel = styled("span", { shouldForwardProp: (p) => p !== "status" })<{
  status: UploadStatus;
}>`
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.09em;
  color: ${({ status }) => labelColor(status)};
  transition: color 0.2s;
`;

const pctColor = (s: UploadStatus): string => {
  if (s === "paused") return "#7B47CC";
  if (s === "cancelled") return "#A32D2D";
  if (s === "completed") return "#2B7A0B";
  return "#1F1F1F";
};

const Pct = styled("div", { shouldForwardProp: (p) => p !== "status" })<{ status: UploadStatus }>`
  font-family: "IBM Plex Mono", monospace;
  font-size: 12px;
  font-weight: 700;
  color: ${({ status }) => pctColor(status)};
  transition: color 0.2s;
  min-width: 36px;
  text-align: right;
`;

const Track = styled("div")`
  flex: 1;
  height: 5px;
  background: #e6e6e2;
  border-radius: 10px;
  overflow: hidden;
`;

const fillColor = (s: UploadStatus): string => {
  if (s === "paused") return "#BC92F8";
  if (s === "cancelled") return "#E24B4A";
  if (s === "completed") return "#2B7A0B";
  return "#2B3501";
};

const TrackFill = styled("div", { shouldForwardProp: (p) => p !== "status" })<{
  status: UploadStatus;
}>`
  height: 100%;
  background: ${({ status }) => fillColor(status)};
  border-radius: 10px;
  transition: width 0.1s linear, background 0.3s;
`;

const UploadMeta = styled("div")`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 6px;
`;

const MetaText = styled("span")`
  font-family: "IBM Plex Mono", monospace;
  font-size: 10px;
  font-weight: 600;
  color: #aaaaaa;
`;

const CtrlGroup = styled("div")`
  display: flex;
  gap: 7px;
  flex-shrink: 0;
  /* on mobile wrap to its own row, right-aligned */
  width: 100%;
  justify-content: flex-end;
  @media (min-width: 768px) {
    width: auto;
    justify-content: flex-start;
  }
`;

const CtrlBtn = styled("button", { shouldForwardProp: (p) => p !== "variant" })<{
  variant: "pause" | "cancel" | "resume";
}>`
  width: 36px;
  height: 36px;
  border-radius: 9px;
  border: 1.5px solid ${({ variant }) => (variant === "resume" ? "rgba(188,146,248,0.5)" : "#E6E6E2")};
  background: ${({ variant }) => (variant === "resume" ? "rgba(188,146,248,0.08)" : "#fff")};
  color: ${({ variant }) => (variant === "resume" ? "#7B47CC" : "#1F1F1F")};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s;
  flex-shrink: 0;
  padding: 0;
  &:hover {
    ${({ variant }) =>
      variant === "pause"
        ? "border-color: #BC92F8; color: #7B47CC; background: rgba(188,146,248,0.08);"
        : ""}
    ${({ variant }) =>
      variant === "cancel"
        ? "border-color: #E24B4A; color: #A32D2D; background: rgba(226,75,74,0.06);"
        : ""}
    ${({ variant }) =>
      variant === "resume" ? "border-color: #7B47CC; background: rgba(188,146,248,0.16);" : ""}
  }
`;

const CancelledActions = styled("div")`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 16px;
  justify-content: center;
`;

const RetryBtn = styled("button")`
  background: #2b3501;
  color: #fff;
  border: none;
  border-radius: 50px;
  font-family: "Inter", sans-serif;
  font-size: 12px;
  font-weight: 600;
  padding: 8px 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: opacity 0.13s;
  &:hover {
    opacity: 0.85;
  }
`;

const DismissBtn = styled("button")`
  background: transparent;
  color: #959a80;
  border: 1.5px solid #e6e6e2;
  border-radius: 50px;
  font-family: "Inter", sans-serif;
  font-size: 12px;
  font-weight: 600;
  padding: 8px 18px;
  cursor: pointer;
  transition: all 0.13s;
  &:hover {
    border-color: #999;
    color: #1f1f1f;
  }
`;

const HiddenInput = styled("input")`
  display: none;
`;

const Preview = styled("img")`
  display: block;
  width: 100%;
  max-width: 400px;
  height: auto;
  max-height: 194px;
  object-fit: contain;

  &.ProfileImagePreview {
    border-radius: 50%;
    max-width: 300px;
  }
  @media (max-width: 900px) {
    display: block;
  }
`;

export default ContentUploadNew;
